import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import { Form } from "@/components/form/form";
import { OrganizationSemestersComboboxFormField } from "@/components/form/form-field/combobox/organization-semesters-combobox-form-field";
import { findEnumName } from "@/lib/enum-utils";
import {
  getAdminClassDayGetAdminClassDaysForSemesterQueryKey,
  getAdminClassDayGetAdminClassDaysForSemesterQueryOptions,
  useAdminClassDayGetAdminClassDaysForSemester,
} from "@/orval/admin-class-day";
import {
  getAdminEnrollmentDayGetEnrollmentDaysForOrganizationQueryKey,
  getAdminEnrollmentDayGetEnrollmentDaysForOrganizationQueryOptions,
  useAdminEnrollmentDayClearEnrollmentDaysForSemester,
  adminEnrollmentDayGetEnrollmentDaysForOrganization,
  useAdminEnrollmentDayRunEnrollmentDayAssignment,
} from "@/orval/admin-enrollment-day";
import { DayOfWeek, type AdminEnrollmentDayListItemDTO } from "@/orval/model";
import type { CustomColumnDef } from "@/types/custom-column-def";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { useMemo, useState } from "react";
import { EnrollmentDayDialog } from "./-components/enrollment-day-dialog";
import { OrganizationFamiliesComboboxFormField } from "@/components/form/form-field/combobox/organization-families-combobox-form-field";
import { Button } from "@/components/ui/button";
import { CheckCheck, ChevronDown, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { convertGradeLevelToString } from "@/lib/string-utiils";
import {
  getAdminOrganizationGetCurrentAndActiveSemesterQueryOptions,
  useAdminOrganizationGetCurrentAndActiveSemester,
} from "@/orval/admin-organization";
import { PortalPageHeader } from "@/components/portal-page-header";
import { z } from "zod";
import { buildDataTableQueryOptions, ensureRouteQueriesData } from "@/lib/route-loader";

const enrollmentDaysSearchSchema = z.object({
  semesterId: z.coerce.number().optional(),
  organizationFamilyId: z.coerce.number().optional(),
  includeGraduates: z.union([z.boolean(), z.stringbool()]).optional(),
});

interface EnrollmentDaysPageFilters {
  semesterId?: number;
  organizationFamilyId?: number;
  includeGraduates?: boolean;
}

function EnrollmentDaysPage() {
  const { organizationId } = Route.useParams();
  const search = Route.useSearch();

  const navigate = useNavigate();
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);
  const { mutateAsync: runEnrollmentAssignment, isPending: isRunningEnrollmentAssignment } =
    useAdminEnrollmentDayRunEnrollmentDayAssignment();
  const { mutateAsync: resetEnrollmentDays, isPending: isResettingEnrollmentDays } =
    useAdminEnrollmentDayClearEnrollmentDaysForSemester();
  const { data: currentAndNextSemester } = useAdminOrganizationGetCurrentAndActiveSemester(organizationId);

  const semesterId =
    (search.semesterId ?? currentAndNextSemester?.nextSemesterId ?? currentAndNextSemester?.activeSemesterId) ||
    undefined;
  const includeGraduates = search.includeGraduates ?? false;

  const defaultFormValues = {
    ...search,
    semesterId,
  };

  const getEnrollmentDays = adminEnrollmentDayGetEnrollmentDaysForOrganization;
  const { data: classDays } = useAdminClassDayGetAdminClassDaysForSemester(
    organizationId,
    semesterId!,
    { take: 20 },
    {
      query: {
        queryKey: getAdminClassDayGetAdminClassDaysForSemesterQueryKey(organizationId, semesterId!, { take: 20 }),
        enabled: !!semesterId,
      },
    },
  );

  const columns = useMemo(() => {
    const columns: CustomColumnDef<AdminEnrollmentDayListItemDTO>[] = [
      {
        header: ({ column }) => <DataTableColumnHeader column={column} title="First Name" />,
        accessorKey: "studentFirstName",
        sortField: "Child.FirstName",
      },
      {
        header: ({ column }) => <DataTableColumnHeader column={column} title="Last Name" />,
        accessorKey: "studentLastName",
        sortField: "Child.LastName",
      },
      {
        header: "Grade Level",
        accessorFn: (row) => convertGradeLevelToString(row.studentGradeLevel),
        id: "gradeLevel",
      },
    ];

    if (classDays) {
      for (const classDay of classDays.items) {
        columns.push({
          header: findEnumName(DayOfWeek, classDay.dayOfWeek),
          cell: ({ row }) => {
            const rowClassDay = row.original.classDays.find((day) => day.classDayId === classDay.id);
            const dateString = rowClassDay ? format(parseISO(rowClassDay.effectiveDate), "MM/dd/yyyy") : null;
            const offsetLabel = rowClassDay?.offset != null ? `(Day: ${rowClassDay.offset + 1})` : "(Default)";
            const status = rowClassDay?.hasOverride ? "(Override)" : offsetLabel;

            return (
              <span>
                {dateString}
                <span className="opacity-60 ml-1">{status}</span>
              </span>
            );
          },
        });
      }
    }

    return columns;
  }, [classDays]);

  const handleRunEnrollmentAssignment = async () => {
    if (!semesterId) {
      return;
    }

    toast.success("Enrollment assignment started.");

    await runEnrollmentAssignment({ organizationId, semesterId: semesterId });

    toast.success("Enrollment assignment completed.");
  };

  const handleResetEnrollmentDays = async () => {
    if (!semesterId) {
      return;
    }

    toast.success("Enrollment days reset started.");

    await resetEnrollmentDays({ organizationId, semesterId: semesterId });

    toast.success("Enrollment days reset completed.");
  };

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <PortalPageHeader
        className="mb-8"
        title="Enrollment Days"
        actions={
          semesterId ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" type="button">
                  Actions
                  <ChevronDown className="size-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={handleRunEnrollmentAssignment}
                  className="text-green-600"
                  disabled={isRunningEnrollmentAssignment || isResettingEnrollmentDays}
                >
                  <CheckCheck className="size-4 mr-2" />
                  Run Enrollment Assignment
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={handleResetEnrollmentDays}
                  className="text-red-600"
                  disabled={isRunningEnrollmentAssignment || isResettingEnrollmentDays}
                >
                  <Trash2 className="size-4 mr-2" />
                  Reset Enrollment Days
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null
        }
      />

      <EnrollmentDayDialog
        isOpen={!!selectedStudentId}
        onClose={() => setSelectedStudentId(null)}
        organizationId={organizationId}
        semesterId={semesterId!}
        studentId={selectedStudentId!}
      />

      <DataTable
        columns={columns}
        renderFilters={
          <Form<EnrollmentDaysPageFilters>
            useFormProps={{ defaultValues: defaultFormValues }}
            onChange={(data) => navigate({ to: ".", search: data })}
            containerClassName="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:w-auto lg:flex lg:flex-row lg:items-center lg:gap-4 lg:gap-y-0"
          >
            <OrganizationSemestersComboboxFormField
              className="min-w-0 w-full lg:w-80"
              name="semesterId"
              organizationId={organizationId}
            />

            <OrganizationFamiliesComboboxFormField
              className="min-w-0 w-full lg:w-80"
              name="organizationFamilyId"
              organizationId={organizationId}
              showClearButton
            />

            <div className="flex min-w-0 items-center gap-2">
              <Switch
                id="include-graduates-toggle"
                checked={includeGraduates}
                onCheckedChange={(checked) =>
                  navigate({
                    to: ".",
                    search: {
                      ...search,
                      includeGraduates: checked ? true : undefined,
                    },
                  })
                }
              />
              <Label htmlFor="include-graduates-toggle" className="cursor-pointer text-sm font-medium mr-2">
                Include Graduates
              </Label>
            </div>
          </Form>
        }
        missingRequiredFilterValues={!semesterId}
        missingRequiredFilterValuesMessage="Please select a semester."
        queryFn={async (params, signal) => {
          const queryParams = {
            ...params,
            organizationFamilyId: search.organizationFamilyId,
            includeGraduates,
          };

          return getEnrollmentDays(
            organizationId,
            semesterId!,
            queryParams as Parameters<typeof getEnrollmentDays>[2],
            signal,
          );
        }}
        queryKeyFn={(params) => {
          const queryParams = {
            ...params,
            organizationFamilyId: search.organizationFamilyId,
            includeGraduates,
          };

          return getAdminEnrollmentDayGetEnrollmentDaysForOrganizationQueryKey(
            organizationId,
            semesterId!,
            queryParams as Parameters<typeof getEnrollmentDays>[2],
          );
        }}
        onRowClick={(row) => setSelectedStudentId(row.studentId)}
        getRowIdFn={(row) => row.studentId.toString()}
      />
    </div>
  );
}

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/enrollment-days")({
  validateSearch: enrollmentDaysSearchSchema,
  loader: async ({ params, location, ...loaderContext }) => {
    const search = enrollmentDaysSearchSchema.parse(location.search);
    let semesterId = search.semesterId;
    if (semesterId === undefined) {
      const semesters = await loaderContext.context.queryClient.ensureQueryData(
        getAdminOrganizationGetCurrentAndActiveSemesterQueryOptions(params.organizationId),
      );
      semesterId = semesters?.nextSemesterId ?? semesters?.activeSemesterId ?? undefined;
    }

    if (!semesterId) {
      return;
    }

    const queryOptions = buildDataTableQueryOptions({});
    return ensureRouteQueriesData(loaderContext, [
      getAdminClassDayGetAdminClassDaysForSemesterQueryOptions(params.organizationId, semesterId, { take: 20 }),
      getAdminEnrollmentDayGetEnrollmentDaysForOrganizationQueryOptions(params.organizationId, semesterId, {
        ...queryOptions,
        organizationFamilyId: search.organizationFamilyId,
        includeGraduates: search.includeGraduates,
      }),
    ]);
  },
  component: EnrollmentDaysPage,
});
